import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, Loading } from "@/components/ui-kit";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function Results() {
  const [rows, setRows] = useState(null);
  const [prog, setProg] = useState("all");

  const load = () => api.get("/tutor/results").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const releaseResults = async (hid) => {
    try {
      const { data } = await api.post(`/homework/${hid}/release-results`);
      toast.success(data.released > 0 ? `Results released to ${data.released} student(s) 🎉` : "No completed attempts to release yet.");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const releaseOne = async (attemptId) => {
    try {
      await api.post(`/attempts/${attemptId}/release-result`);
      toast.success("Result released to student 🎉");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  if (!rows) return <Loading />;

  const filtered = rows.filter((r) => prog === "all" || r.program === prog);
  // group by homework
  const groups = {};
  filtered.forEach((r) => {
    groups[r.homework_id] = groups[r.homework_id] || { title: r.title, section: r.section_name, program: r.program_name, week: r.week, rows: [] };
    groups[r.homework_id].rows.push(r);
  });

  return (
    <div className="animate-fade-up">
      <PageHeader title="Results" subtitle="Class performance across released homework."
        right={
          <Select value={prog} onValueChange={setProg}>
            <SelectTrigger data-testid="results-program-filter" className="h-10 w-56 rounded-xl"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All programs</SelectItem>
              <SelectItem value="jmss">JMSS Year 10</SelectItem>
              <SelectItem value="selective">Year 9 Selective</SelectItem>
            </SelectContent>
          </Select>
        } />

      {Object.keys(groups).length === 0 ? (
        <div className="ember-card py-16 text-center text-sm text-[#948EAB]">No released MCQ homework yet.</div>
      ) : (
        <div className="space-y-6">
          {Object.entries(groups).map(([hid, g]) => (
            <div key={hid} className="ember-card overflow-hidden">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[#F1EEFA] p-5">
                <div>
                  <h3 className="font-heading font-bold text-[#1E1B2E]">{g.title}</h3>
                  <p className="text-xs text-[#948EAB]">{g.program} · {g.section}</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs font-semibold text-[#7C3AED]">
                    {g.rows.filter((r) => r.status === "complete").length}/{g.rows.length} completed
                  </span>
                  {(() => {
                    const done = g.rows.filter((r) => r.status === "complete");
                    const held = done.filter((r) => !r.results_released);
                    if (done.length === 0) return null;
                    return held.length > 0
                      ? <Button size="sm" data-testid={`release-results-${hid}`} onClick={() => releaseResults(hid)} className="h-9 rounded-xl ember-gradient text-xs font-bold">Release results ({held.length})</Button>
                      : <span className="rounded-full bg-[#ECFDF5] px-2.5 py-1 text-[11px] font-bold text-[#059669]">Results released</span>;
                  })()}
                </div>
              </div>
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead>Student</TableHead>
                    <TableHead>Section</TableHead>
                    <TableHead className="text-right">Score</TableHead>
                    <TableHead className="text-right">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {g.rows.map((r) => (
                    <TableRow key={r.student_id} data-testid={`result-row-${hid}-${r.student_id}`}>
                      <TableCell>
                        <Link to={`/tutor/students/${r.student_id}`} className="flex items-center gap-2.5">
                          <Avatar className="h-8 w-8"><AvatarImage src={r.avatar_url || undefined} /><AvatarFallback className="bg-[#EDE9FE] text-[10px] font-bold text-[#5B21B6]">{r.student_name.split(" ").map((x) => x[0]).join("")}</AvatarFallback></Avatar>
                          <span className="font-semibold text-[#1E1B2E]">{r.student_name}</span>
                        </Link>
                      </TableCell>
                      <TableCell className="text-[#524B6B]">{r.section_name}</TableCell>
                      <TableCell className="text-right font-heading font-bold">{r.percentage != null ? <span className="text-[#5B21B6]">{Math.round(r.percentage)}%</span> : <span className="text-[#C4B5FD]">—</span>}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          {r.status === "complete" && (
                            <Link to={`/tutor/attempts/${r.attempt_id}`} data-testid={`view-attempt-${r.attempt_id}`} className="rounded-lg border border-[#DDD6FE] px-2.5 py-1 text-[11px] font-bold text-[#5B21B6] hover:bg-[#F5F3FF]">View / Mark</Link>
                          )}
                          {r.status === "complete"
                            ? (r.results_released
                                ? <span className="rounded-full bg-[#ECFDF5] px-2.5 py-1 text-[11px] font-bold text-[#059669]">Released</span>
                                : <>
                                    <span className="rounded-full bg-[#FFFBEB] px-2.5 py-1 text-[11px] font-bold text-[#B45309]">Marked · held</span>
                                    <Button size="sm" variant="outline" data-testid={`release-one-${r.attempt_id}`} onClick={() => releaseOne(r.attempt_id)} className="h-7 rounded-lg text-[11px] font-bold">Release</Button>
                                  </>)
                            : <span className="rounded-full bg-[#F3F4F6] px-2.5 py-1 text-[11px] font-bold text-[#6B7280]">Not Started</span>}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
